import { ActionIcon, Box, Drawer, TextInput } from '@mantine/core';
import { useDisclosure, useHotkeys } from '@mantine/hooks';
import { IconX } from '@tabler/icons-react';
import type { CollectionStepIndex } from 'components/CollectionStep';
import { FilterButton } from 'components/FilterButton';
import { PageTitle } from 'components/PageTitle';
import { i18nKeys, useTranslation } from 'locales';
import { isDefined } from 'remeda';
import { STEP_TO_STEP_THEME } from 'shared';
import { useEnhancedNavigation } from 'shared/hooks/utils';
import { useLoadKanbanDebtors } from '../api';
import { DebtorKanbanCard, Kanban, KanbanLoadingState } from '../components';
import { DebtorKanbanFilters } from '../components/debtor/DebtorKanbanFilters';
import { countActiveFilters } from '../utils/filters';

export const DebtorKanban = () => {
  const { t } = useTranslation();

  const { columns, filtersForm } = useLoadKanbanDebtors();
  const [isFiltersDrawerOpen, filtersDrawerHandlers] = useDisclosure(false);
  const enhancedNavigate = useEnhancedNavigation();

  useHotkeys([['f', filtersDrawerHandlers.toggle]]);

  const isFetching = Object.values(columns).some(column => column.isDebtorColumnFetching);
  const areAllColumnsPopulated = Object.values(columns).every(column =>
    isDefined(column.debtorColumn),
  );

  if (!areAllColumnsPopulated)
    return <KanbanLoadingState title={t(i18nKeys.KANBAN.DEBTOR.TITLE)} columnCount={6} />;

  const activeFiltersCount = countActiveFilters(filtersForm.values, ['column']);

  const cols = Object.values(columns).map((column, colIndex) => {
    const cards = column.debtorColumn!.debtors.map((debtor, debtorIndex) => (
      <Kanban.Card
        key={debtorIndex}
        onClick={enhancedNavigate(`/clients/${debtor.id}#balance-detail`)}
      >
        <DebtorKanbanCard debtor={debtor} />
      </Kanban.Card>
    ));

    return (
      <Kanban.Column
        isLoading={isFetching}
        key={colIndex}
        step={colIndex as CollectionStepIndex}
        total={column.debtorColumn!.total.amount ?? 0.0}
        currency={column.debtorColumn!.total.currency ?? 'EUR'}
        color={STEP_TO_STEP_THEME[colIndex as CollectionStepIndex].color}
      >
        {cards}
      </Kanban.Column>
    );
  });

  return (
    <>
      {/* Fixed height necessary to constrain Kanban columns, This is the natural height of the component without actions */}
      <Box h="60px">
        <PageTitle>
          {t(i18nKeys.KANBAN.DEBTOR.TITLE)}
          <PageTitle.Actions>
            <TextInput
              w={250}
              rightSection={
                filtersForm.values.fullName ? (
                  <ActionIcon
                    variant="light"
                    c="gray"
                    onClick={() => filtersForm.setFieldValue('fullName', '')}
                  >
                    <IconX stroke={1.5} size={16} />
                  </ActionIcon>
                ) : null
              }
              placeholder={t(i18nKeys.KANBAN.FILTERS.CLIENT_NAME_PH)}
              {...filtersForm.getInputProps('fullName')}
            />
            <FilterButton
              activeFiltersCount={activeFiltersCount}
              openFilters={filtersDrawerHandlers.open}
              resetFilters={filtersForm.reset}
            />
          </PageTitle.Actions>
        </PageTitle>
      </Box>
      <Box h="calc(100% - 60px)">
        <Kanban minColumnWidth={180}>{cols}</Kanban>
      </Box>
      <Drawer
        position="right"
        opened={isFiltersDrawerOpen}
        onClose={filtersDrawerHandlers.close}
        title={t(i18nKeys.FILTERS)}
        keepMounted
        padding="lg"
        styles={{
          title: {
            fontSize: '1.5rem',
          },
        }}
      >
        <DebtorKanbanFilters form={filtersForm} />
      </Drawer>
    </>
  );
};
